import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";

import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";

import { Input } from "./Input";
import Loading from "./Loading";

import { checkCep } from "../services/app";
import { branchRegister } from "../services/branches";

import {
  formatCpfCnpj,
  formatPhone,
  maskCep,
  phoneValidate,
  unMask,
  validateCpfCnpj,
} from "../helpers/format";

const BranchRegister = ({
  open,
  onClose,
  refetch,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: branchRegister,
    onSuccess: () => {
      toast.success("Filial criada com sucesso");

      refetch();
      resetForm();

      setTimeout(() => {
        onClose();
      }, 300);
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { values, handleSubmit, handleChange, setFieldValue, resetForm } =
    useFormik({
      initialValues: {
        name: "",
        document: "",
        email: "",
        phone: "",
        state: "",
        city: "",
        neighborhood: "",
        street: "",
        number: "",
        complement: "",
        cep: "",
        birthDate: "",
      },
      onSubmit: (values) => {
        const requiredFields = [
          { field: values.name, label: "Nome" },
          { field: values.document, label: "CPF" },
          { field: values.phone, label: "Telefone" },
          { field: values.email, label: "E-mail" },
          { field: values.cep, label: "CEP" },
          { field: values.street, label: "Rua" },
          { field: values.number, label: "Número" },
          { field: values.neighborhood, label: "Bairro" },
          { field: values.city, label: "Cidade" },
          { field: values.state, label: "Estado" },
        ];

        for (const item of requiredFields) {
          if (!item.field || item.field.trim() === "") {
            toast.error(`Preencha o campo "${item.label}"`);
            return;
          }
        }

        if (!validateCpfCnpj(unMask(values.document))) {
          toast.error("CPF/CNPJ inválido");
          return;
        }

        if (!phoneValidate(formatPhone(values.phone))) {
          toast.error("Telefone inválido");
          return;
        }

        mutate({
          ...values,
          phone: unMask(values.phone),
          cep: unMask(values.cep),
          document: unMask(values.document),
        });
      },
    });

  const {
    refetch: refetchCep,
    data,
    isFetching,
  } = useQuery({
    queryFn: async () => await checkCep(values.cep),
    queryKey: ["getCep", values.cep],
    enabled: false,
    retry: 1,
  });

  useEffect(() => {
    if (data) {
      setFieldValue("neighborhood", data?.neighborhood);
      setFieldValue("state", data?.state);
      setFieldValue("street", data?.address);
      setFieldValue("city", data?.city);
    }
  }, [data]);

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={() => {
        onClose();
        resetForm();
      }}
      fullWidth
    >
      <Loading loading={isPending || isFetching} />
      <DialogContent>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={26} fontWeight={600}>
              Cadastrar filial
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton
              onClick={() => {
                onClose();
                resetForm();
              }}
            >
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>
          <Grid2 size={6}>
            <Input
              id="name"
              name="name"
              label="Nome"
              placeholder="Digite o nome da sua filial"
              type="text"
              fullWidth
              size="small"
              value={values.name}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={6}>
            <Input
              id="document"
              name="document"
              label="CPF/CNPJ ( pode ser o mesmo da matriz )"
              placeholder="Digite o CPF/CNPJ"
              type="text"
              fullWidth
              size="small"
              value={formatCpfCnpj(values.document)}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={6}>
            <Input
              id="phone"
              name="phone"
              label="Telefone"
              placeholder="Digite o E-Telefone"
              type="text"
              fullWidth
              size="small"
              value={formatPhone(values.phone)}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={6}>
            <Input
              id="email"
              name="email"
              label="E-mail"
              placeholder="Digite o E-mail"
              type="text"
              fullWidth
              size="small"
              value={values.email}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>
          <Grid2 size={12}>
            <Typography fontWeight={700}>Endereço</Typography>
          </Grid2>
          <Grid2 size={5}>
            <Input
              id="cep"
              name="cep"
              label="CEP"
              placeholder="Digite o CEP"
              type="text"
              fullWidth
              size="small"
              value={maskCep(values.cep)}
              onChange={handleChange}
              onBlur={(e) => {
                if (e.target.value.length) {
                  refetchCep();
                }
              }}
            />
          </Grid2>
          <Grid2 size={7}>
            <Input
              id="street"
              name="street"
              label="Rua"
              placeholder="Digite a rua"
              type="text"
              fullWidth
              size="small"
              value={values.street}
              onChange={handleChange}
            />
          </Grid2>{" "}
          <Grid2 size={7}>
            <Input
              id="neighborhood"
              name="neighborhood"
              label="Bairro"
              placeholder="Digite o bairro"
              type="text"
              fullWidth
              size="small"
              value={values.neighborhood}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={5}>
            <Input
              id="number"
              name="number"
              label="Número"
              placeholder="Digite a número"
              type="text"
              fullWidth
              size="small"
              value={values.number}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={4}>
            <Input
              id="city"
              name="city"
              label="Cidade"
              placeholder="Digite a cidade"
              type="text"
              fullWidth
              size="small"
              value={values.city}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={4}>
            <Input
              id="state"
              name="state"
              label="Estado"
              placeholder="Digite o estado"
              type="text"
              fullWidth
              size="small"
              value={values.state}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={4}>
            <Input
              id="complement"
              name="complement"
              label="Complemento (Opcional)"
              placeholder="Digite o complemento"
              type="text"
              fullWidth
              value={values.complement}
              onChange={handleChange}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions>
        <Grid2 size={12}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => handleSubmit()}
          >
            Cadastrar aluno
          </Button>
        </Grid2>
      </DialogActions>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default BranchRegister;
